import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Wordmark from '../components/Wordmark';

const steps = [
  {
    title: 'Welcome to GhostLight',
    content: (alias) => (
      <>
        <p style={{ color: 'rgba(237,228,211,0.62)', fontSize: '14px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.7, margin: '0 0 12px 0' }}>
          GhostLight is a pseudonymous support space built exclusively for live entertainment professionals. The people who keep the show running.
        </p>
        <p style={{ color: 'rgba(237,228,211,0.62)', fontSize: '14px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.7, margin: 0 }}>
          You are <span style={{ color: '#e8b547', fontFamily: '"Instrument Serif", serif' }}>{alias}</span> here. That's your identity on the platform — persistent, recognizable, and yours.
        </p>
      </>
    )
  },
  {
    title: 'Pseudonymous, Not Anonymous',
    content: () => (
      <>
        <p style={{ color: 'rgba(237,228,211,0.62)', fontSize: '14px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.7, margin: '0 0 12px 0' }}>
          Your alias is public. Your real identity is private. We protect it — with a few honest exceptions:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0 0 16px 0' }}>
          <div style={{ display: 'flex', gap: '8px', color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6 }}>
            <span style={{ color: '#e8b547', flexShrink: 0 }}>&mdash;</span>
            <span>Your name and email are visible only to NINJA administrators</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6 }}>
            <span style={{ color: '#e8b547', flexShrink: 0 }}>&mdash;</span>
            <span>Moderators see your alias only — never your real identity</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6 }}>
            <span style={{ color: '#e8b547', flexShrink: 0 }}>&mdash;</span>
            <span>Identity is disclosed only under valid legal compulsion or a credible, specific threat to life</span>
          </div>
        </div>
        <p style={{ color: 'rgba(237,228,211,0.38)', fontSize: '12px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6, margin: 0 }}>
          Our standing commitment: full anonymity isn't possible on a platform with legal obligations. We won't pretend otherwise.
        </p>
      </>
    )
  },
  {
    title: 'What This Space Is',
    content: () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <p style={{ color: '#e8b547', fontSize: '14px', fontFamily: '"Instrument Serif", serif', margin: '0 0 4px 0' }}>The Stage</p>
          <p style={{ color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6, margin: 0 }}>Community feed. Chronological. No likes, no followers, no algorithm. Just people.</p>
        </div>
        <div>
          <p style={{ color: '#e8b547', fontSize: '14px', fontFamily: '"Instrument Serif", serif', margin: '0 0 4px 0' }}>The Green Room</p>
          <p style={{ color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6, margin: 0 }}>Structured debriefs. Guided prompts to process the show. Private by default.</p>
        </div>
        <div>
          <p style={{ color: '#e8b547', fontSize: '14px', fontFamily: '"Instrument Serif", serif', margin: '0 0 4px 0' }}>Ghost Hours</p>
          <p style={{ color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6, margin: 0 }}>End-to-end encrypted peer chat. Matched by department. Ephemeral by design.</p>
        </div>
        <div>
          <p style={{ color: '#e8b547', fontSize: '14px', fontFamily: '"Instrument Serif", serif', margin: '0 0 4px 0' }}>The Toolkit</p>
          <p style={{ color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6, margin: 0 }}>Curated mental health resources. Industry-specific. No sponsored content, ever.</p>
        </div>
      </div>
    )
  },
  {
    title: 'What This Space Is Not',
    content: () => (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0 0 16px 0' }}>
          {[
            'Not a social network. No clout. No followers.',
            'Not therapy. We never position ourselves as clinical support.',
            'Not a crisis service — but we always surface crisis resources.',
            'Not monetized through ads or data. Ever.',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', color: 'rgba(237,228,211,0.52)', fontSize: '13px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.6 }}>
              <span style={{ color: 'rgba(237,228,211,0.38)', flexShrink: 0 }}>&mdash;</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p style={{ color: 'rgba(237,228,211,0.62)', fontSize: '14px', fontFamily: '"Geist", "Inter", system-ui, sans-serif', lineHeight: 1.7, margin: 0 }}>
          This space lives or dies on trust.<br/>Take care of it, and it'll take care of you.
        </p>
      </>
    )
  }
];

export default function Onboarding({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0e0a08',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>

        {step === 0 && (
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Wordmark amber />
          </div>
        )}

        {/* Card */}
        <div style={{
          background: '#120c09',
          border: '1px solid rgba(237,228,211,0.08)',
          borderRadius: '12px',
          padding: '32px',
        }}>
          <h2 style={{
            fontFamily: '"Instrument Serif", serif',
            fontSize: '22px',
            fontWeight: 400,
            color: '#e8b547',
            margin: '0 0 20px 0',
          }}>
            {current.title}
          </h2>

          {current.content(user.alias)}

          {/* Footer: step dots + nav buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '32px',
          }}>
            {/* Step indicators */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {steps.map((_, i) => (
                <div key={i} style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: i === step ? '#e8b547' : 'rgba(237,228,211,0.08)',
                  transition: 'background 0.3s ease',
                }} />
              ))}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {step > 0 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(237,228,211,0.38)',
                    fontSize: '13px',
                    fontFamily: '"Geist", "Inter", system-ui, sans-serif',
                    cursor: 'pointer',
                    padding: '8px 12px',
                  }}
                >
                  Back
                </button>
              )}
              <button
                onClick={() => isLast ? onComplete() : setStep(s => s + 1)}
                style={{
                  border: '1px solid #e8b547',
                  background: 'rgba(232,181,71,0.16)',
                  color: '#e8b547',
                  fontFamily: '"Geist", "Inter", system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background 0.3s ease',
                }}
              >
                {isLast ? 'Enter GhostLight' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
